import { useEffect, useMemo, useRef } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { equipmentInventoryApi } from '@entities/equipmentInventory';

import { HISTORY_LOGS_COUNT_PER_PAGE } from './consts';

export const useHistoryLogs = (inventoryGuid: string | null, isOpen: boolean) => {
    const { t } = useTranslation();
    const loadMoreRef = useRef<HTMLLIElement>(null);
    const logsQuery = useInfiniteQuery({
        ...equipmentInventoryApi.loadInventoryLogsInfiniteQueryOptions({
            inventoryGuid: inventoryGuid ?? '',
            countPerPage: HISTORY_LOGS_COUNT_PER_PAGE,
        }),
        enabled: isOpen && Boolean(inventoryGuid),
    });
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = logsQuery;
    const logs = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data?.pages]);

    useEffect(() => {
        const node = loadMoreRef.current;

        if (!node || !hasNextPage || isFetchingNextPage) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    void fetchNextPage();
                }
            },
            { root: node.parentElement?.parentElement ?? null, rootMargin: '40px' }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    return { t, logsQuery, logs, loadMoreRef };
};
